import styled from "@emotion/styled";
import { ClickAwayListener } from "@mui/material";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import { FC, useCallback, useEffect, useState } from "react";
import { useShadow } from "src/shared/hooks";
import { ConditionalWrapper, ScrollableBox } from "../..";
import { IDrawerComponentProps } from "../model/types";
import DrawerHeader from "./DrawerHeader";

const navigationWidth = 320;

const Dragger = styled("div")`
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  width: 5px;
  cursor: ew-resize;
`;

export const DrawerComponent: FC<IDrawerComponentProps> = ({
  width = 400,
  title,
  children,
  headerStatus,
  footer,
  resizable = false,
  header,
  drawerOpenTarget,
  hideDrawerHeader,
  onClose,
  closeOnBackdropClick,
  position,
  titleOverflowVisible = false,
  headerActionId,
  headerActionItems,
  ...drawerProps
}) => {
  const [isResizingMode, setIsResizingMode] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState(width);
  const { scrollRef, hasScroll } = useShadow(drawerProps.open);
  const isPersistent = drawerProps.variant === "persistent";
  const isOverlapping = drawerProps.variant === "persistent-overlapping";
  const handleMousedown = () => {
    setIsResizingMode(true);
  };

  const handleMousemove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingMode || !resizable) {
        return;
      }

      const offsetRight =
        document.body.offsetWidth - (e.clientX - document.body.offsetLeft);
      const minWidth = 300;
      const maxWidth = window.innerWidth;
      if (offsetRight > minWidth && offsetRight < maxWidth) {
        setDrawerWidth(offsetRight);
      }
    },
    [isResizingMode, resizable],
  );

  const handleMouseup = useCallback(() => {
    document.removeEventListener("mousemove", handleMousemove);
    setIsResizingMode(false);
  }, [handleMousemove]);

  useEffect(() => {
    if (isResizingMode && resizable) {
      document.addEventListener("mousemove", handleMousemove);
      document.addEventListener("mouseup", handleMouseup);
    }

    return () => document.removeEventListener("mousemove", handleMousemove);
  }, [handleMouseup, handleMousemove, isResizingMode, resizable]);

  return (
    <Box
      sx={{
        overflow: "hidden",
        flexShrink: isPersistent ? 0 : "auto",
      }}
    >
      <Drawer
        {...drawerProps}
        transitionDuration={{ enter: 200, exit: 200 }}
        onClose={(_e, reason) => {
          if (
            (reason === "backdropClick" && !closeOnBackdropClick) ||
            !onClose
          ) {
            return;
          }
          onClose(_e, reason);
        }}
        variant={isPersistent || isOverlapping ? "persistent" : undefined}
        sx={{
          ...drawerProps.sx,
          position: () => {
            if (isPersistent) {
              return "relative";
            }
          },
          height: isPersistent ? "100%" : "auto",
          flexShrink: 1,
          ["& *"]: { userSelect: isResizingMode ? "none" : "auto" },
          width: () => {
            if (isPersistent) {
              return drawerProps.open ? drawerWidth : 0;
            }
            if (isOverlapping) {
              return 0;
            }

            return drawerWidth;
          },

          ...(!drawerProps.open &&
            !isResizingMode && {
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.leavingScreen,
                }),
            }),
          ...(drawerProps.open &&
            !isResizingMode && {
              transition: (theme) =>
                theme.transitions.create("width", {
                  easing: theme.transitions.easing.easeOut,
                  duration: theme.transitions.duration.enteringScreen,
                }),
            }),
          maxWidth: { xs: "100vw", md: `calc(100vw - ${navigationWidth}px)` },
        }}
        PaperProps={{
          sx: {
            height: () => {
              if (isOverlapping) {
                return "auto";
              }

              return undefined;
            },

            minHeight: () => {
              if (isOverlapping) {
                return "100%";
              }

              return undefined;
            },
            width: drawerWidth,
            maxWidth: {
              xs: "100vw",
              md: isPersistent ? `calc(100vw - ${navigationWidth}px)` : "100vw",
            },
            position: () => {
              if (position) {
                return position;
              }
              if (isPersistent) {
                return "static";
              }
              if (isOverlapping) {
                return "fixed";
              }

              return "fixed";
            },
            boxShadow: isOverlapping ? 4 : 0,
            borderLeft: (theme) => {
              if (isPersistent) {
                return `3px solid ${theme.palette.grey[200]}`;
              }

              return "none";
            },
          },
        }}
      >
        <ConditionalWrapper
          condition={
            !!closeOnBackdropClick && isOverlapping && !!drawerProps.open
          }
          wrapper={(content) => (
            <ClickAwayListener
              onClickAway={(e) => {
                const target = e.target as HTMLElement;
                const isDrawerOpenBtn = target.closest(
                  `[data-target="${drawerOpenTarget}"]`,
                );
                if (!isDrawerOpenBtn && !!onClose && drawerProps.open) {
                  onClose(e, "backdropClick");
                }
              }}
            >
              {content}
            </ClickAwayListener>
          )}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              height: "100%",
              position: resizable ? "relative" : "static",
            }}
          >
            {resizable && (
              <Dragger id="dragger" onMouseDown={handleMousedown} />
            )}
            {!hideDrawerHeader && (
              <DrawerHeader
                title={title}
                hasShadow={hasScroll}
                headerStatus={headerStatus}
                titleOverflowVisible={titleOverflowVisible}
                headerActionId={headerActionId}
                headerActionItems={headerActionItems}
                onCrossClick={
                  onClose
                    ? (e) => {
                        if (onClose) {
                          onClose(e, "backdropClick");
                        }
                      }
                    : undefined
                }
              >
                {header}
              </DrawerHeader>
            )}
            <ScrollableBox
              scrollRef={scrollRef}
              flex={1}
              minHeight={0}
              py={hasScroll && !hideDrawerHeader ? 7 : 0}
            >
              {drawerProps.open && children}
            </ScrollableBox>
            {!!footer && (
              <Box
                sx={{
                  p: 6.5,
                  boxShadow: hasScroll ? 7 : "none",
                }}
              >
                {footer}
              </Box>
            )}
          </Box>
        </ConditionalWrapper>
      </Drawer>
    </Box>
  );
};
